import React, { useState, useEffect } from 'react';
import { useAppSettings } from '../contexts/AppSettingsContext';
import './Settings.css';

const Settings = () => {
  const {
    dataSource,
    isSimulator,
    switchingSource,
    setDataSource,
    comingSoonEnabled,
    switchingComingSoon,
    setComingSoon,
    comingSoonOpeningDate,
    switchingOpeningDate,
    setComingSoonOpeningDate,
    eventRequestNotificationEmail,
    eventRequestNotificationEmailFallback,
    deploymentStage,
    savingEventRequestNotificationEmail,
    setEventRequestNotificationEmail,
  } = useAppSettings();
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [localDate, setLocalDate] = useState(comingSoonOpeningDate || '');
  const [localEventEmail, setLocalEventEmail] = useState(eventRequestNotificationEmail || '');
  const [eventEmailError, setEventEmailError] = useState(null);
  const [eventEmailSuccess, setEventEmailSuccess] = useState(null);

  const handleToggle = async () => {
    const newSource = isSimulator ? 'toast' : 'simulator';
    try {
      setError(null);
      setSuccessMessage(null);
      await setDataSource(newSource);
      setSuccessMessage(`Data source switched to ${newSource === 'simulator' ? 'Simulator' : 'Toast POS (LIVE)'}`);
    } catch (err) {
      setError('Failed to switch data source. Please try again.');
    }
  };

  const handleComingSoonToggle = async () => {
    try {
      setError(null);
      setSuccessMessage(null);
      const next = !comingSoonEnabled;
      await setComingSoon(next);
      setSuccessMessage(next
        ? 'Coming Soon screen is now live — users see only the Chula\'s logo.'
        : 'Coming Soon screen disabled — app is back online.');
    } catch (err) {
      setError('Failed to update Coming Soon state. Please try again.');
    }
  };

  useEffect(() => {
    setLocalDate(comingSoonOpeningDate || '');
  }, [comingSoonOpeningDate]);

  useEffect(() => {
    setLocalEventEmail(eventRequestNotificationEmail || '');
  }, [eventRequestNotificationEmail]);

  const formatDateForInput = (isoStr) => {
    if (!isoStr) return '';
    const d = new Date(isoStr);
    if (isNaN(d.getTime())) return '';
    const offset = d.getTimezoneOffset();
    const local = new Date(d.getTime() - offset * 60000);
    return local.toISOString().slice(0, 16);
  };

  const handleDateChange = (e) => {
    const val = e.target.value;
    setLocalDate(val);
    const utc = val ? new Date(val).toISOString() : null;
    setComingSoonOpeningDate(utc);
  };

  const handleEventEmailSave = async (event) => {
    event.preventDefault();
    const normalized = localEventEmail.trim().toLowerCase();
    const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized);
    const isProduction = ['prod', 'production'].includes(String(deploymentStage).toLowerCase());

    setEventEmailError(null);
    setEventEmailSuccess(null);
    if (!validEmail) {
      setEventEmailError('Enter a valid email address.');
      return;
    }
    if (!isProduction && !normalized.endsWith('@twoloons.llc')) {
      setEventEmailError('Dev and beta notification addresses must use @twoloons.llc.');
      return;
    }

    try {
      await setEventRequestNotificationEmail(normalized);
      setLocalEventEmail(normalized);
      setEventEmailSuccess('Event request notification email saved.');
    } catch (err) {
      setEventEmailError('Failed to save the event request notification email. Please try again.');
    }
  };

  return (
    <div className="settings-page">
      <header className="settings-header">
        <h1>Settings</h1>
      </header>

      <div className="settings-section">
        <h2 className="settings-section-title">App Launch</h2>
        <p className="settings-section-description">
          When enabled, the customer app (iOS + web) shows only the Chula's logo and blocks access to all other features until you turn it off.
        </p>

        <div className="data-source-toggle">
          <div className="toggle-header">
            <span className="toggle-label">Coming Soon</span>
            <span className={`toggle-status ${comingSoonEnabled ? 'status-live' : 'status-sim'}`}>
              {comingSoonEnabled ? 'ON' : 'OFF'}
            </span>
          </div>
          <div className="switch-container">
            <span className={`switch-option ${!comingSoonEnabled ? 'active' : ''}`}>Live</span>
            <button
              className={`toggle-switch ${comingSoonEnabled ? 'active' : ''}`}
              onClick={handleComingSoonToggle}
              disabled={switchingComingSoon}
              aria-label="Toggle Coming Soon screen"
            >
              <span className="switch-track">
                <span className="switch-thumb">
                  {switchingComingSoon && <span className="switch-loading"></span>}
                </span>
              </span>
            </button>
            <span className={`switch-option ${comingSoonEnabled ? 'active' : ''}`}>Coming Soon</span>
          </div>
          <p className="toggle-description">
            {comingSoonEnabled
              ? 'Customer app is currently showing the Coming Soon screen.'
              : 'Customer app is live and fully accessible.'}
          </p>

          <div className="coming-soon-date-picker">
            <label className="date-picker-label" htmlFor="opening-datetime">
              Opening Date &amp; Time
            </label>
            <input
              type="datetime-local"
              id="opening-datetime"
              className="date-picker-input"
              value={formatDateForInput(localDate)}
              onChange={handleDateChange}
              disabled={switchingOpeningDate}
            />
            {comingSoonOpeningDate ? (
              <p className="date-picker-hint">
                Customers will see a countdown to{" "}
                {new Date(comingSoonOpeningDate).toLocaleString()}
              </p>
            ) : (
              <p className="date-picker-hint">
                Set a date to show a countdown on the overlay
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="settings-section">
        <h2 className="settings-section-title">Event Requests</h2>
        <p className="settings-section-description">
          New private-room and performer requests are sent to this inbox. It is also the Reply-To address on customer approval and rejection emails. Each environment keeps its own setting.
        </p>

        <form className="event-email-setting" onSubmit={handleEventEmailSave}>
          <label htmlFor="event-request-notification-email">
            Event Request Notification Email
          </label>
          <div className="event-email-row">
            <input
              id="event-request-notification-email"
              type="email"
              value={localEventEmail}
              onChange={(event) => setLocalEventEmail(event.target.value)}
              placeholder={eventRequestNotificationEmailFallback}
              autoComplete="email"
              disabled={savingEventRequestNotificationEmail}
              required
            />
            <button
              type="submit"
              className="settings-save-button"
              disabled={savingEventRequestNotificationEmail}
            >
              {savingEventRequestNotificationEmail ? 'Saving…' : 'Save'}
            </button>
          </div>
          <p className="event-email-effective">
            {eventRequestNotificationEmail
              ? <>Currently sending admin notifications to <strong>{eventRequestNotificationEmail}</strong>.</>
              : <>No override saved. Notifications use <strong>{eventRequestNotificationEmailFallback}</strong>.</>}
          </p>
          {!['prod', 'production'].includes(String(deploymentStage).toLowerCase()) && (
            <p className="event-email-environment-note">
              {String(deploymentStage).toUpperCase()} safety rule: this admin-notification address must end in @twoloons.llc.
            </p>
          )}
          {eventEmailError && <div className="settings-error">{eventEmailError}</div>}
          {eventEmailSuccess && <div className="settings-success">{eventEmailSuccess}</div>}
        </form>
      </div>

      <div className="settings-section">
        <h2 className="settings-section-title">Data Source</h2>
        <p className="settings-section-description">
          Switch between simulator data and live Toast POS data. This affects all pages that display Toast data, including Menu, Analytics, and more.
        </p>

        <div className="data-source-toggle">
          <div className="toggle-header">
            <span className="toggle-label">Data Source</span>
            <span className={`toggle-status ${dataSource === 'toast' ? 'status-live' : 'status-sim'}`}>
              {dataSource === 'toast' ? 'LIVE' : 'SIM'}
            </span>
          </div>
          <div className="switch-container">
            <span className={`switch-option ${isSimulator ? 'active' : ''}`}>Simulator</span>
            <button
              className={`toggle-switch ${dataSource === 'toast' ? 'active' : ''}`}
              onClick={handleToggle}
              disabled={switchingSource}
              aria-label="Toggle data source"
            >
              <span className="switch-track">
                <span className="switch-thumb">
                  {switchingSource && <span className="switch-loading"></span>}
                </span>
              </span>
            </button>
            <span className={`switch-option ${dataSource === 'toast' ? 'active' : ''}`}>Toast</span>
          </div>
          <p className="toggle-description">
            {dataSource === 'toast'
              ? 'Using live data from Toast POS system'
              : 'Using simulator data for testing'}
          </p>
        </div>

        {error && <div className="settings-error">{error}</div>}
        {successMessage && <div className="settings-success">{successMessage}</div>}
      </div>
    </div>
  );
};

export default Settings;
